import { BaseApiService } from '@/shared/api/BaseApiService';
import { createServiceToken } from '@/app/di';
import type { ApiRequestConfig } from '@/shared/api/types';

export interface TreatmentView {
  episode: TreatmentEpisode;
  steps: TreatmentStep[];
}

export interface TreatmentEpisode {
  id: string;
  organizationId: string;
  clinicId: string;
  displayId: string;
  patientId: string;
  procedureId: string;
  toothNumber: string | null;
  toothCount: number;
  surface: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  assignedDentistId: string | null;
  startedAt: string;
  completedAt: string | null;
  abandonedAt: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface TreatmentStep {
  id: string;
  organizationId: string;
  clinicId: string;
  patientProcedureId: string;
  procedureStepId: string | null;
  stepSequence: number;
  name: string;
  pricePaise: number;
  taxRateBps: number;
  attemptNo: number;
  status: 'pending' | 'completed' | 'skipped' | 'not_applicable';
  skipReason: string | null;
  isRedo: boolean;
  completedAt: string | null;
  appointmentId: string | null;
  /** ISO datetime from linked appointment.scheduledAt (when enriched). */
  appointmentScheduledAt: string | null;
  /** Appointment type code from linked appointment (when enriched). */
  appointmentType: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface TreatmentListResponse {
  items: TreatmentView[];
  total: number;
}

export interface StartTreatmentInput {
  patientId: string;
  procedureId: string;
  toothNumbers?: string[];
  surface?: string;
  assignedDentistId?: string;
}

export interface CompleteStepInput {
  workDoneNotes?: string;
  appointmentId: string;
}

export interface RelinkStepAppointmentInput {
  appointmentId: string;
  workDoneNotes?: string;
}

export interface SkipStepInput {
  skipReason: string;
  skipType?: 'skipped' | 'not_applicable';
}

export interface RedoStepInput {
  procedureStepId: string;
}

export interface UpdateStepPriceInput {
  pricePaise: number;
}

export interface UpdateEpisodeStatusInput {
  status: 'completed' | 'abandoned';
}

export interface CatalogProcedure {
  id: string;
  code: string;
  name: string;
  billingGranularity: string;
  defaultTaxRateBps: number;
  isActive: boolean;
  deletedAt: string | null;
}

export interface CatalogItemWithSteps extends CatalogProcedure {
  steps: CatalogStep[];
}

export interface CatalogStep {
  id: string;
  name: string;
  feePaise: number;
  taxRateBps: number | null;
  sessionOrder: number;
  stepSequence?: number;
  durationMinutes?: number;
  requiresDentist?: boolean;
  isOptional?: boolean;
}

export interface IPatientTreatmentRepository {
  listByPatient(patientId: string, params?: { page?: number; limit?: number }): Promise<TreatmentListResponse>;
  getById(patientId: string, id: string): Promise<TreatmentView>;
  startTreatment(patientId: string, input: StartTreatmentInput): Promise<TreatmentView>;
  completeStep(stepId: string, input?: CompleteStepInput): Promise<TreatmentView>;
  relinkStepAppointment(stepId: string, input: RelinkStepAppointmentInput): Promise<TreatmentView>;
  skipStep(stepId: string, input: SkipStepInput): Promise<TreatmentView>;
  redoStep(episodeId: string, input: RedoStepInput): Promise<TreatmentView>;
  abandon(episodeId: string): Promise<TreatmentView>;
  updateStepPrice(stepId: string, input: UpdateStepPriceInput): Promise<TreatmentView>;
  updateEpisodeStatus(episodeId: string, input: UpdateEpisodeStatusInput): Promise<TreatmentView>;
  getLinkedTreatments(appointmentId: string): Promise<TreatmentView[]>;
}

export const PATIENT_TREATMENT_SERVICE = createServiceToken<IPatientTreatmentRepository>('PATIENT_TREATMENT_SERVICE');

export class PatientTreatmentService extends BaseApiService implements IPatientTreatmentRepository {
  protected readonly resourcePath = 'patients';

  private async getG<T>(path: string, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this.client.get<T>(path, config);
    return data;
  }

  private async postG<T>(path: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this.client.post<T>(path, body, config);
    return data;
  }

  private async patchG<T>(path: string, body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this.client.patch<T>(path, body, config);
    return data;
  }

  async listByPatient(patientId: string, params?: { page?: number; limit?: number }): Promise<TreatmentListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return this.getG<TreatmentListResponse>(`/patients/${patientId}/treatments?${query.toString()}`);
  }

  async getById(patientId: string, id: string): Promise<TreatmentView> {
    return this.getG<TreatmentView>(`/patients/${patientId}/treatments/${id}`);
  }

  async startTreatment(patientId: string, input: StartTreatmentInput): Promise<TreatmentView> {
    return this.postG<TreatmentView>(`/patients/${patientId}/treatments`, {
      ...input,
      toothNumbers: input.toothNumbers?.length ? input.toothNumbers : undefined,
    });
  }

  async completeStep(stepId: string, input?: CompleteStepInput): Promise<TreatmentView> {
    return this.postG<TreatmentView>(`/treatments/steps/${stepId}/complete`, input ?? {});
  }

  async relinkStepAppointment(
    stepId: string,
    input: RelinkStepAppointmentInput,
  ): Promise<TreatmentView> {
    return this.patchG<TreatmentView>(`/treatments/steps/${stepId}/appointment`, input);
  }

  async skipStep(stepId: string, input: SkipStepInput): Promise<TreatmentView> {
    return this.postG<TreatmentView>(`/treatments/steps/${stepId}/skip`, input);
  }

  async redoStep(episodeId: string, input: RedoStepInput): Promise<TreatmentView> {
    return this.postG<TreatmentView>(`/treatments/${episodeId}/redo`, input);
  }

  async abandon(episodeId: string): Promise<TreatmentView> {
    return this.patchG<TreatmentView>(`/treatments/${episodeId}/abandon`);
  }

  async updateStepPrice(stepId: string, input: UpdateStepPriceInput): Promise<TreatmentView> {
    return this.patchG<TreatmentView>(`/treatments/steps/${stepId}`, input);
  }

  async updateEpisodeStatus(episodeId: string, input: UpdateEpisodeStatusInput): Promise<TreatmentView> {
    return this.patchG<TreatmentView>(`/treatments/${episodeId}/status`, input);
  }

  async getLinkedTreatments(appointmentId: string): Promise<TreatmentView[]> {
    return this.getG<TreatmentView[]>(`/appointments/${appointmentId}/treatments`);
  }
}
